/**
 * Server-side session context.
 *
 * Authentication is owned by the API: `/auth/login` and `/auth/logout` are
 * full-page redirects, and the session lives in an HttpOnly cookie scoped to
 * the app's registrable domain. This provider discovers the session by
 * calling `GET /api/v1/me` once at boot — a 200 means signed in, a 401 means
 * signed out — and exposes the same hook surface the AuthKit integration used,
 * so the rest of the app is provider-agnostic.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "./api-client";
import type { MeResponse, UserProfile } from "./auth";

interface SessionContextValue {
  /** Signed-in user, or `null` when signed out. */
  user: UserProfile | null;
  /** `true` until the initial `me` query settles. */
  isLoading: boolean;
  /** The org the session's WorkOS token is scoped to, when known. */
  organizationId: string | null;
  /** Full-page redirect to the API's hosted-AuthKit login. */
  signIn: () => void;
  /** Full-page redirect to the API's logout (revokes the WorkOS session). */
  signOut: () => void;
}

const SessionContext = createContext<SessionContextValue>({
  user: null,
  isLoading: true,
  organizationId: null,
  signIn: () => undefined,
  signOut: () => undefined,
});

/** Where the auth round-trip starts. */
export function loginUrl(): string {
  return `${import.meta.env.VITE_API_BASE_URL ?? ""}/auth/login`;
}

/** Where sessions are ended. */
export function logoutUrl(): string {
  return `${import.meta.env.VITE_API_BASE_URL ?? ""}/auth/logout`;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((data) => {
        if (cancelled) return;
        setMe(data);
        // Seed the same cache entry `useMe` reads, so the boot-time `me`
        // discovery is the only `/api/v1/me` request.
        queryClient.setQueryData(["me", data.user.id], data);
      })
      .catch(() => {
        // 401 is the normal signed-out state; anything else is already
        // logged by the API client — still renders the signed-out UI.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  const signIn = useCallback(() => {
    window.location.assign(loginUrl());
  }, []);
  const signOut = useCallback(() => {
    window.location.assign(logoutUrl());
  }, []);

  const user = me?.user ?? null;
  const organizationId = me?.activeOrganizationId ?? null;

  const value = useMemo<SessionContextValue>(
    () => ({ user, isLoading, organizationId, signIn, signOut }),
    [user, isLoading, organizationId, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  return useContext(SessionContext);
}
